import type { EmergencyContact, EmergencyDrill, EmergencyEquipment, EmergencyIncident, EmergencyNotification, EmergencyPlan, EmergencyResponseAction } from "../../services/emergencyService";
import { byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Emergencies and safety: incidents move reported → acknowledged → response
 * under way → resolved → closed (or straight to closed as a false alarm);
 * response actions are given to staff; alerts go out in the app; drills,
 * plans and equipment keep the school ready. Mirrors apps/emergency/views.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const time = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const stamp = (value?: string | null) => (value ? `${value.slice(0, 10)} ${value.slice(11, 16)}` : "");
export const weekday = (value?: string | null) => {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
export const addDays = (iso: string, days: number) => {
  const [y, m, d] = iso.split("-").map(Number);
  const next = new Date(y, m - 1, d + days);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
};

type Pairs = [string, string][];
export const SEVERITIES: Pairs = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["critical", "Critical"]];
export const ACTION_TYPES: Pairs = [["evacuation", "Evacuation"], ["lockdown", "Lockdown"], ["medical_response", "Medical response"], ["fire_response", "Fire response"], ["security_response", "Security response"], ["communication", "Communication"], ["other", "Other"]];
export const CONTACT_TYPES: Pairs = [["police", "Police"], ["fire", "Fire brigade"], ["ambulance", "Ambulance"], ["hospital", "Hospital or health centre"], ["medical", "Doctor or clinic"], ["school_nurse", "School nurse"], ["security", "Security"], ["administration", "School administration"], ["other", "Other"]];
export const ALERT_TYPES: Pairs = [["alert", "Alert: act now"], ["warning", "Warning"], ["information", "Information"], ["all_clear", "All clear"]];
export const AUDIENCES: Pairs = [["all", "Everyone"], ["staff", "All staff"], ["teachers", "Teachers"], ["parents", "Parents"], ["students", "Learners"], ["emergency_team", "The response team"]];
export const EQUIPMENT_STATUS: Pairs = [["available", "Ready"], ["in_use", "In use"], ["maintenance", "Needs maintenance"], ["damaged", "Damaged"], ["missing", "Missing"]];
export const CONDITIONS: Pairs = [["available", "Ready for use"], ["maintenance", "Needs maintenance"], ["damaged", "Damaged"], ["missing", "Missing"]];
export const labelOf = (pairs: Pairs, value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value ? value.replaceAll("_", " ") : "—");
export const SEVERITY_TONE: Record<string, string> = { low: "text-gray-600", medium: "text-gray-900", high: "font-semibold text-amber-800", critical: "font-semibold text-red-700" };

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

// ---- Incidents ------------------------------------------------------------------------------

export const isFalseAlarm = (row: Pick<EmergencyIncident, "status" | "resolution_notes">) => row.status === "closed" && Boolean(row.resolution_notes?.startsWith("False alarm"));
export const incidentOpen = (row: Pick<EmergencyIncident, "status">) => row.status !== "resolved" && row.status !== "closed";

export function incidentStatus(row: Pick<EmergencyIncident, "status" | "resolution_notes">): Tone {
  if (isFalseAlarm(row)) return tone("False alarm", "bg-gray-400");
  return ({ reported: tone("Reported, not yet acknowledged", "bg-red-500"), acknowledged: tone("Acknowledged", "bg-amber-500"), in_progress: tone("Response under way", "bg-sky-500"), resolved: tone("Resolved", "bg-emerald-500"), closed: tone("Closed", "bg-gray-400") } as Record<string, Tone>)[row.status];
}

/** What the safety team can do with an incident now. */
export function incidentActions(row: EmergencyIncident, manager: boolean) {
  return {
    acknowledge: manager && row.status === "reported",
    start: manager && row.status === "acknowledged",
    falseAlarm: manager && (row.status === "reported" || row.status === "acknowledged"),
    assign: manager && (row.status === "acknowledged" || row.status === "in_progress"),
    resolve: manager && row.status === "in_progress" && row.open_responses === 0,
    resolveBlocked: manager && row.status === "in_progress" && row.open_responses > 0,
    close: manager && row.status === "resolved",
    alert: manager && row.status !== "closed",
  };
}

// ---- Response actions ------------------------------------------------------------------------

export function actionStatus(row: Pick<EmergencyResponseAction, "status">): Tone {
  return ({ assigned: tone("Given out", "bg-amber-500"), in_progress: tone("Being done", "bg-sky-500"), completed: tone("Done", "bg-emerald-500"), cancelled: tone("Cancelled", "bg-gray-400") } as Record<string, Tone>)[row.status];
}

export function actionActions(row: EmergencyResponseAction, me: number | undefined, manager: boolean) {
  const mine = manager || row.assigned_to === me;
  return {
    start: mine && row.status === "assigned" && (row.incident_status === "acknowledged" || row.incident_status === "in_progress"),
    complete: mine && row.status === "in_progress",
    cancel: mine && (row.status === "assigned" || row.status === "in_progress"),
  };
}

// ---- Drills, plans, equipment -----------------------------------------------------------------

export function drillStatus(row: Pick<EmergencyDrill, "status" | "scheduled_date">, today: string): Tone {
  if (row.status === "scheduled") return row.scheduled_date < today ? tone("Overdue", "bg-red-500") : row.scheduled_date === today ? tone("Today", "bg-amber-500") : tone("Planned", "bg-sky-500");
  return ({ in_progress: tone("Under way", "bg-indigo-500"), completed: tone("Held", "bg-emerald-500"), cancelled: tone("Cancelled", "bg-gray-400") } as Record<string, Tone>)[row.status];
}

export const turnout = (row: Pick<EmergencyDrill, "expected_participants" | "actual_participants">) =>
  row.expected_participants ? Math.round((row.actual_participants / row.expected_participants) * 100) : null;

export function planStatus(row: Pick<EmergencyPlan, "is_approved" | "is_active" | "next_review">, today: string): Tone {
  if (!row.is_active) return tone("Retired", "bg-gray-400");
  if (!row.is_approved) return tone("Draft, awaiting approval", "bg-amber-500");
  if (row.next_review && row.next_review <= today) return tone("Review due", "bg-red-500");
  return tone("Approved", "bg-emerald-500");
}

export function equipmentStatus(row: Pick<EmergencyEquipment, "status" | "next_inspection" | "last_inspection" | "expiry_date">, today: string): Tone {
  if (row.status === "missing") return tone("Missing", "bg-red-500");
  if (row.status === "damaged") return tone("Damaged", "bg-red-500");
  if (row.status === "maintenance") return tone("Needs maintenance", "bg-amber-500");
  if (row.expiry_date && row.expiry_date < today) return tone("Expired", "bg-red-500");
  if (!row.last_inspection || (row.next_inspection && row.next_inspection <= today)) return tone("Check due", "bg-amber-500");
  return tone("Ready", "bg-emerald-500");
}

export function alertStatus(row: Pick<EmergencyNotification, "is_sent">): Tone {
  return row.is_sent ? tone("Sent", "bg-emerald-500") : tone("Ready to send", "bg-amber-500");
}

// ---- Registers ---------------------------------------------------------------------------------

const severityRank: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export const incidentConfig: RegisterConfig<EmergencyIncident> = {
  search: (row) => `${row.title} ${row.location} ${row.incident_type_name} ${row.description} ${row.reported_by_name}`,
  views: [
    { key: "open", label: "Open", match: incidentOpen },
    { key: "new", label: "Not acknowledged", match: (row) => row.status === "reported" },
    { key: "serious", label: "High or critical", match: (row) => incidentOpen(row) && (row.severity === "high" || row.severity === "critical") },
    { key: "resolved", label: "Resolved", match: (row) => row.status === "resolved" },
    { key: "closed", label: "Closed", match: (row) => row.status === "closed" },
    { key: "all", label: "All" },
  ],
  date: { label: "Happened", value: (row) => row.incident_date },
  category: { label: "Kind", value: (row) => row.incident_type_name },
  sorts: [
    { key: "severity", label: "Most serious first", compare: (a, b) => Number(!incidentOpen(a)) - Number(!incidentOpen(b)) || severityRank[a.severity] - severityRank[b.severity] || b.reported_time.localeCompare(a.reported_time) },
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => `${row.incident_date}${row.incident_time}`)) },
  ],
};

export function actionConfig(me?: number): RegisterConfig<EmergencyResponseAction> {
  const open = (row: EmergencyResponseAction) => row.status === "assigned" || row.status === "in_progress";
  return {
    search: (row) => `${row.title} ${row.description} ${row.incident_title} ${row.assigned_to_name}`,
    views: [
      { key: "open", label: "Open", match: open },
      { key: "mine", label: "Mine", match: (row) => row.assigned_to === me && open(row) },
      { key: "done", label: "Done", match: (row) => row.status === "completed" },
      { key: "all", label: "All" },
    ],
    date: { label: "Given", value: (row) => row.action_time },
    category: { label: "Kind", value: (row) => labelOf(ACTION_TYPES, row.action_type) },
    sorts: [
      { key: "severity", label: "Most serious incident first", compare: (a, b) => severityRank[a.incident_severity] - severityRank[b.incident_severity] || a.action_time.localeCompare(b.action_time) },
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.action_time)) },
    ],
  };
}

export const contactConfig: RegisterConfig<EmergencyContact> = {
  search: (row) => `${row.name} ${labelOf(CONTACT_TYPES, row.contact_type)} ${row.phone_number} ${row.alternative_phone ?? ""} ${row.service_area ?? ""}`,
  views: [
    { key: "active", label: "In use", match: (row) => row.is_active },
    { key: "primary", label: "Call first", match: (row) => row.is_active && row.is_primary },
    { key: "retired", label: "Not in use", match: (row) => !row.is_active },
  ],
  category: { label: "Service", value: (row) => labelOf(CONTACT_TYPES, row.contact_type) },
  sorts: [
    { key: "primary", label: "Call-first first", compare: (a, b) => Number(b.is_primary) - Number(a.is_primary) || a.name.localeCompare(b.name) },
    { key: "fastest", label: "Fastest to arrive", compare: (a, b) => a.response_time_minutes - b.response_time_minutes },
  ],
};

export function planConfig(today: string): RegisterConfig<EmergencyPlan> {
  return {
    search: (row) => `${row.name} ${row.emergency_type_name} ${row.description ?? ""} ${row.plan_content}`,
    views: [
      { key: "current", label: "In force", match: (row) => row.is_active && row.is_approved },
      { key: "draft", label: "Awaiting approval", match: (row) => row.is_active && !row.is_approved },
      { key: "review", label: "Review due", match: (row) => row.is_active && row.is_approved && Boolean(row.next_review) && row.next_review! <= today },
      { key: "retired", label: "Retired", match: (row) => !row.is_active },
      { key: "all", label: "All" },
    ],
    category: { label: "Emergency", value: (row) => row.emergency_type_name },
    sorts: [{ key: "name", label: "Name A–Z", compare: byText((row) => row.name) }, { key: "review", label: "Review soonest", compare: byText((row) => row.next_review || "9999") }],
  };
}

export const alertConfig: RegisterConfig<EmergencyNotification> = {
  search: (row) => `${row.title} ${row.message} ${row.incident_title}`,
  views: [{ key: "draft", label: "Ready to send", match: (row) => !row.is_sent }, { key: "sent", label: "Sent", match: (row) => row.is_sent }, { key: "all", label: "All" }],
  date: { label: "Sent", value: (row) => row.sent_time ?? row.created_at },
  category: { label: "For", value: (row) => labelOf(AUDIENCES, row.target_audience) },
  sorts: [{ key: "newest", label: "Newest first", compare: reversed(byText((row) => row.sent_time ?? row.created_at)) }],
};

export function drillConfig(today: string): RegisterConfig<EmergencyDrill> {
  return {
    search: (row) => `${row.name} ${row.drill_type_name} ${row.description ?? ""} ${row.results ?? ""}`,
    views: [
      { key: "planned", label: "Planned", match: (row) => row.status === "scheduled" || row.status === "in_progress" },
      { key: "due", label: "Due now", match: (row) => row.status === "scheduled" && row.scheduled_date <= today },
      { key: "held", label: "Held", match: (row) => row.status === "completed" },
      { key: "all", label: "All" },
    ],
    date: { label: "Day", value: (row) => row.scheduled_date },
    category: { label: "Emergency", value: (row) => row.drill_type_name },
    sorts: [{ key: "soonest", label: "Soonest first", compare: byText((row) => `${row.scheduled_date}${row.scheduled_time}`) }, { key: "latest", label: "Latest first", compare: reversed(byText((row) => `${row.scheduled_date}${row.scheduled_time}`)) }],
  };
}

export function equipmentConfig(today: string): RegisterConfig<EmergencyEquipment> {
  return {
    search: (row) => `${row.name} ${row.location} ${row.room ?? ""} ${row.serial_number ?? ""} ${row.equipment_type_name}`,
    views: [
      { key: "all", label: "All" },
      { key: "attention", label: "Needs attention", match: (row) => equipmentStatus(row, today).label !== "Ready" },
      { key: "check", label: "Check due", match: (row) => row.status !== "missing" && (!row.last_inspection || (Boolean(row.next_inspection) && row.next_inspection! <= today)) },
      { key: "expiring", label: "Expiring within 30 days", match: (row) => Boolean(row.expiry_date) && row.expiry_date! <= addDays(today, 30) },
    ],
    category: { label: "Where", value: (row) => row.location },
    sorts: [{ key: "check", label: "Check soonest", compare: byText((row) => (row.last_inspection ? row.next_inspection || "9999" : "0000")) }, { key: "name", label: "Name A–Z", compare: byText((row) => row.name) }],
  };
}
